import * as React from 'react';
import { useLayoutEffect } from '../../../../hooks/use-isomorphic-layout-effect';
import type { FilterState } from '../components/action-bar/filter-menu';
import { DEFAULT_PAGE_INDEX, DEFAULT_SORT_COLUMN } from '../data-grid.constants';
import type { DataGridBaseRow, DataGridColumnDef, DataGridColumnType } from '../data-grid.type';
import { OrderByEnum } from '../data-grid.type';
import type {
	IDataGridUrlColumnMeta,
	IDataGridUrlDateRange,
	IDataGridUrlState,
	IDataGridUrlSyncDefaults,
	IDataGridUrlSyncOptions,
	UrlSyncHistoryMode,
} from '../util/url-sync';
import {
	URL_SYNC_CONTEXT_PARAM,
	activeFiltersToUrlFilters,
	areSearchStringsEqual,
	buildColumnMetaFromDefs,
	contextualizeDataGridUrlSearch,
	filtersRecordToActiveShape,
	getUrlSyncParamPrefix,
	isValidUrlSyncScope,
	parseDataGridUrlSearch,
	prefixParam,
	serializeDataGridUrlSearch,
	stripLeadingQuestion,
	toUrlCalendarDate,
} from '../util/url-sync';
import type { IDataGridExternalState } from './use-data-grid';

/**
 * Columns that carry no sortable value. They are still column defs, so `sortable` is left unset on
 * them across the app; keeping them out of `orderBy` here stops a crafted URL from sending a
 * non-column name such as `actions` to the list API.
 */
const NON_SORTABLE_COLUMN_TYPES: ReadonlySet<DataGridColumnType> = new Set(['actions', 'filter-only']);

interface IUseDataGridUrlSyncInput<T extends DataGridBaseRow> {
	columns: DataGridColumnDef<T>[];
	defaultSortColumn?: string;
	defaultSortDirection?: OrderByEnum;
	urlSync?: IDataGridUrlSyncOptions;
	defaultDateRange?: IDataGridUrlDateRange;
}

/** The grid's list state, as the grid itself holds it. */
export interface IDataGridCommittedState {
	activeFilters: Record<string, FilterState>;
	hiddenColumns: Set<string>;
	pageIndex: number;
	searchQuery: string;
	sortColumn: string;
	sortDirection: OrderByEnum;
	dateFrom?: string;
	dateTo?: string;
}

export interface IUseDataGridUrlSyncResult {
	/**
	 * URL state read once at mount, or `undefined` when sync is off. Feeds the grid's initial
	 * state; the grid owns it from then on, until an external navigation remounts it.
	 */
	initialState?: IDataGridExternalState;
	/** Writes the grid's current state to the URL. No-op when sync is off. */
	commit: (state: IDataGridCommittedState) => void;
	/**
	 * Registers a listener for URL changes the grid did not cause (back/forward, an address-bar
	 * edit) that alter this grid's list state. The grid remounts in response so it hydrates from
	 * the new URL the same way it does on landing. Returns a teardown. No-op when sync is off.
	 */
	subscribeToExternalNavigation: (onNavigate: () => void) => () => void;
}

const EMPTY_COLUMN_META: IDataGridUrlColumnMeta[] = [];

/**
 * Two-way translation between a DataGrid's list state and the query string.
 *
 * This sits above the grid: it reads the URL once at mount to produce initial state, serializes
 * committed state back out, and reports inbound URL changes so the grid can remount from them. It
 * never holds grid state of its own, so the grid stays the single owner.
 */
export function useDataGridUrlSync<T extends DataGridBaseRow>({
	columns,
	defaultSortColumn,
	defaultSortDirection,
	urlSync,
	defaultDateRange,
}: IUseDataGridUrlSyncInput<T>): IUseDataGridUrlSyncResult {
	const isScopeUsable = urlSync != null && isValidUrlSyncScope(urlSync.scope) && urlSync.routeKey.trim().length > 0;
	const [isScopeClaimed, setIsScopeClaimed] = React.useState(false);
	const canHydrate = isScopeUsable && urlSync.enabled !== false;
	const isEnabled = canHydrate && isScopeClaimed;
	const coordinator = canHydrate ? urlSync.coordinator : undefined;
	const contextKey = urlSync?.contextKey;

	const paramPrefix = React.useMemo(
		() => (isScopeUsable ? getUrlSyncParamPrefix(urlSync.scope) : undefined),
		[isScopeUsable, urlSync?.scope]
	);
	const contextParam = paramPrefix != null ? prefixParam(paramPrefix, URL_SYNC_CONTEXT_PARAM) : undefined;

	const isDifferentContext = React.useCallback(
		(search: string): boolean => {
			if (contextParam == null) {
				return false;
			}
			const urlContext = new URLSearchParams(stripLeadingQuestion(search)).get(contextParam);
			return urlContext != null && urlContext !== contextKey;
		},
		[contextKey, contextParam]
	);

	const columnMeta = React.useMemo(
		() => (isScopeUsable ? buildColumnMetaFromDefs(columns) : EMPTY_COLUMN_META),
		[columns, isScopeUsable]
	);

	const sortableColumnKeys = React.useMemo(
		() =>
			columns
				.filter(column => column.sortable !== false && !NON_SORTABLE_COLUMN_TYPES.has(column.type))
				.map(column => column.key),
		[columns]
	);

	const defaults = React.useMemo<IDataGridUrlSyncDefaults>(
		() => ({
			pageIndex: DEFAULT_PAGE_INDEX,
			search: '',
			order: defaultSortDirection ?? OrderByEnum.DESC,
			orderBy: defaultSortColumn ?? DEFAULT_SORT_COLUMN,
			filters: Object.fromEntries(columnMeta.map(column => [column.paramName, column.defaultValues])),
			hiddenColumnKeys: columns.filter(column => column.hidden === true).map(column => column.key),
			dateFrom: toUrlCalendarDate(defaultDateRange?.startDate, urlSync?.calendarDates),
			dateTo: toUrlCalendarDate(defaultDateRange?.endDate, urlSync?.calendarDates),
		}),
		[columnMeta, columns, defaultDateRange, defaultSortColumn, defaultSortDirection, urlSync?.calendarDates]
	);

	/**
	 * Query string this hook last wrote. An inbound notification matching it is our own echo, not
	 * a navigation the grid needs to follow.
	 */
	const lastWrittenSearchRef = React.useRef<string | undefined>(undefined);

	/**
	 * Params belonging to a different data set are dropped before parsing, so a history entry from
	 * another context (a different facility, say) cannot hydrate this grid.
	 */
	const readContextualizedSearch = React.useCallback((): string => {
		const search = stripLeadingQuestion(coordinator?.getSearch() ?? '');
		if (contextKey == null || contextParam == null || paramPrefix == null) {
			return search;
		}
		return contextualizeDataGridUrlSearch({ search, paramPrefix, contextKey });
	}, [contextKey, contextParam, coordinator, paramPrefix]);

	const parseSearch = React.useCallback(
		(search: string): IDataGridUrlState =>
			parseDataGridUrlSearch({
				search,
				paramPrefix,
				columns: columnMeta,
				defaults,
				sortableColumnKeys,
				calendarDates: urlSync?.calendarDates,
			}),
		[columnMeta, defaults, paramPrefix, sortableColumnKeys, urlSync?.calendarDates]
	);

	const [initialUrlState] = React.useState<IDataGridUrlState | undefined>(() => {
		if (!isScopeUsable || urlSync.enabled === false) {
			return undefined;
		}
		return parseSearch(readContextualizedSearch());
	});

	/**
	 * Column visibility driven by a `hidden` predicate is derived from filter state, so it is the
	 * grid's to compute and must never be written to or restored from the URL.
	 */
	const syncableHiddenColumnKeys = React.useMemo(
		() => new Set(columns.filter(column => typeof column.hidden !== 'function').map(column => column.key)),
		[columns]
	);

	const toExternalState = React.useCallback(
		(urlState: IDataGridUrlState): IDataGridExternalState => ({
			activeFilters: filtersRecordToActiveShape(urlState.filters, columnMeta),
			hiddenColumnKeys: urlState.hiddenColumnKeys,
			pageIndex: urlState.pageIndex,
			searchQuery: urlState.search,
			sortColumn: urlState.orderBy,
			sortDirection: urlState.order,
			dateFrom: urlState.dateFrom,
			dateTo: urlState.dateTo,
		}),
		[columnMeta]
	);

	const toUrlState = React.useCallback(
		(state: IDataGridCommittedState): IDataGridUrlState => ({
			filters: activeFiltersToUrlFilters(state.activeFilters),
			hiddenColumnKeys: Array.from(state.hiddenColumns).filter(key => syncableHiddenColumnKeys.has(key)),
			pageIndex: state.pageIndex,
			search: state.searchQuery,
			order: state.sortDirection,
			orderBy: state.sortColumn,
			dateFrom: state.dateFrom ?? defaults.dateFrom,
			dateTo: state.dateTo ?? defaults.dateTo,
		}),
		[defaults, syncableHiddenColumnKeys]
	);

	const serialize = React.useCallback(
		(state: IDataGridUrlState): string => {
			const next = serializeDataGridUrlSearch({
				currentSearch: coordinator?.getSearch() ?? '',
				paramPrefix,
				columns: columnMeta,
				defaults,
				state,
			});
			if (contextParam == null || paramPrefix == null || contextKey == null) {
				return next;
			}
			const params = new URLSearchParams(next);
			const ownsAnyParam = Array.from(params.keys()).some(key => key !== contextParam && key.startsWith(paramPrefix));
			// Tag the entry only once it carries grid state, so a plain page visit stays clean.
			if (ownsAnyParam) {
				params.set(contextParam, contextKey);
			} else {
				params.delete(contextParam);
			}
			return params.toString();
		},
		[columnMeta, contextKey, contextParam, coordinator, defaults, paramPrefix]
	);

	const write = React.useCallback(
		(search: string, history: UrlSyncHistoryMode) => {
			if (coordinator == null || areSearchStringsEqual(coordinator.getSearch(), search)) {
				return;
			}
			lastWrittenSearchRef.current = search;
			coordinator.setSearch(search, { history });
		},
		[coordinator]
	);

	/**
	 * Undefined until the grid commits, so the first commit replaces the landing entry instead of
	 * pushing a duplicate one the user would have to press Back through.
	 */
	const lastCommittedStateRef = React.useRef<IDataGridUrlState | undefined>(undefined);

	const commit = React.useCallback(
		(state: IDataGridCommittedState) => {
			if (!isEnabled) {
				return;
			}
			const next = toUrlState(state);
			const previous = lastCommittedStateRef.current;
			lastCommittedStateRef.current = next;

			// Only the mount echo defers to an entry describing another data set, because the grid
			// that owns it may still be mounting. Anything after that is a change made in this grid,
			// which takes the URL over rather than leaving the user's view unshareable.
			if (previous === undefined && isDifferentContext(coordinator?.getSearch() ?? '')) {
				return;
			}

			// A re-render that left list state untouched must not write. Back/forward and address-bar
			// edits re-render the grid before it remounts from the new URL, and a write here would put
			// the pre-navigation state back over what the user just navigated to.
			if (previous !== undefined && areUrlStatesEqual(previous, next)) {
				return;
			}

			const history = resolveHistoryMode({ previous, next });
			write(serialize(next), history);
		},
		[coordinator, isDifferentContext, isEnabled, serialize, toUrlState, write]
	);

	const isExternalNavigation = React.useCallback((): boolean => {
		const search = stripLeadingQuestion(coordinator?.getSearch() ?? '');
		// Single-use: consuming the echo lets a later navigation back to that same URL through.
		if (lastWrittenSearchRef.current != null && areSearchStringsEqual(search, lastWrittenSearchRef.current)) {
			lastWrittenSearchRef.current = undefined;
			return false;
		}
		lastWrittenSearchRef.current = undefined;
		// Leave an entry for another data set intact. The surrounding context change will
		// mount the matching grid, which can hydrate this entry without the old grid first
		// replacing it with its own defaults.
		if (isDifferentContext(search)) {
			return false;
		}
		// Another grid's params changed; remounting this one would refetch and drop its selection
		// for nothing.
		const previous = lastCommittedStateRef.current;
		return previous === undefined || !areUrlStatesEqual(previous, parseSearch(readContextualizedSearch()));
	}, [coordinator, isDifferentContext, parseSearch, readContextualizedSearch]);

	const isExternalNavigationRef = React.useRef(isExternalNavigation);
	useLayoutEffect(() => {
		isExternalNavigationRef.current = isExternalNavigation;
	}, [isExternalNavigation]);

	// Stable across re-renders on purpose: a grid that re-subscribed while another grid's remount
	// flushed its effects would miss the navigation being delivered.
	const subscribeToExternalNavigation = React.useCallback(
		(onNavigate: () => void) => {
			if (!isEnabled || coordinator == null) {
				return () => undefined;
			}
			return coordinator.subscribeToExternalChanges(() => {
				if (isExternalNavigationRef.current()) {
					onNavigate();
				}
			});
		},
		[coordinator, isEnabled]
	);

	const routeKey = urlSync?.routeKey;
	const scope = urlSync?.scope;

	React.useEffect(() => {
		if (coordinator == null || routeKey == null || scope == null) {
			return;
		}
		const registration = coordinator.register({ routeKey, scope });
		setIsScopeClaimed(registration.isAccepted);

		return () => {
			registration.release();
			setIsScopeClaimed(false);
		};
	}, [coordinator, routeKey, scope]);

	/**
	 * Rewrite the landing URL once, so invalid or default-valued params are normalized before the
	 * user can share the link back.
	 */
	const hasNormalizedLandingUrlRef = React.useRef(false);
	React.useEffect(() => {
		if (!isEnabled || hasNormalizedLandingUrlRef.current || initialUrlState == null) {
			return;
		}
		// The entry describes another data set, so the grid that owns it is the one that may
		// rewrite it. Normalizing to this grid's defaults would replace it before that grid mounts.
		if (isDifferentContext(coordinator?.getSearch() ?? '')) {
			return;
		}
		hasNormalizedLandingUrlRef.current = true;
		write(serialize(initialUrlState), 'replace');
	}, [coordinator, initialUrlState, isDifferentContext, isEnabled, serialize, write]);

	const initialState = React.useMemo(
		() => (canHydrate && initialUrlState != null ? toExternalState(initialUrlState) : undefined),
		[canHydrate, initialUrlState, toExternalState]
	);

	return { initialState, commit, subscribeToExternalNavigation };
}

interface IResolveHistoryModeInput {
	previous?: IDataGridUrlState;
	next: IDataGridUrlState;
}

/**
 * Search keystrokes replace the current entry; every other change pushes a new one, so Back
 * returns to the previous view rather than the previous character.
 */
function resolveHistoryMode({ previous, next }: IResolveHistoryModeInput): UrlSyncHistoryMode {
	if (previous == null) {
		return 'replace';
	}
	const isSearchChange = previous.search !== next.search;
	if (!isSearchChange) {
		return 'push';
	}
	const isOnlySearchChange =
		previous.order === next.order &&
		previous.orderBy === next.orderBy &&
		next.pageIndex === DEFAULT_PAGE_INDEX &&
		areFilterMapsEqual(previous.filters, next.filters) &&
		areKeyListsEqual(previous.hiddenColumnKeys, next.hiddenColumnKeys) &&
		previous.dateFrom === next.dateFrom &&
		previous.dateTo === next.dateTo;

	return isOnlySearchChange ? 'replace' : 'push';
}

function areUrlStatesEqual(a: IDataGridUrlState, b: IDataGridUrlState): boolean {
	return (
		a.pageIndex === b.pageIndex &&
		a.search === b.search &&
		a.order === b.order &&
		a.orderBy === b.orderBy &&
		areKeyListsEqual(a.hiddenColumnKeys, b.hiddenColumnKeys) &&
		areFilterMapsEqual(a.filters, b.filters) &&
		a.dateFrom === b.dateFrom &&
		a.dateTo === b.dateTo
	);
}

function areFilterMapsEqual(a: Record<string, string[]>, b: Record<string, string[]>): boolean {
	const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
	for (const key of keys) {
		if (!areKeyListsEqual(a[key] ?? [], b[key] ?? [])) {
			return false;
		}
	}
	return true;
}

function areKeyListsEqual(a: string[], b: string[]): boolean {
	if (a.length !== b.length) {
		return false;
	}
	const sortedB = [...b].sort();
	return [...a].sort().every((value, index) => value === sortedB[index]);
}
